/* @flow */

import {serializeFilters} from 'nutshell-core';
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {transformDataForReport} from 'nutshell-core/json-to-report-data';
import {safelyParseError} from 'nutshell-core/utils';
import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {ENGAGEMENT_SALES} from '../../report-constants';

import {addReportSpecificFilters} from './helpers';
import {
    updateReportChartData,
    failReportChartData,
    updateListData,
    updateListSummaryData,
    failListData,
} from './sales-actions';
import type {
    EngagementSalesReportDataRequestedAction,
    EngagementSalesListRequestedAction,
} from './types';

const REST_URL = '/rest/leads/report';

export const requestEngagementSalesReportDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('ENGAGEMENT_SALES_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: EngagementSalesReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;

            const adjustedReportParams = {...reportParams, dateRangeType: 'closedTime'};
            const params = getPostParams(adjustedReportParams, filters);

            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                catchError((err) => Observable.of(failReportChartData(err)))
            );
        })
    );

export const requestEngagementSalesListDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('ENGAGEMENT_SALES_LIST_DATA_REQUESTED').pipe(
        switchMap((action: EngagementSalesListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const filtersWithOriginAndStatus = addReportSpecificFilters(filters);

            const listRequestStream = Observable.from(
                fetchList('leads', {
                    filter: filtersWithOriginAndStatus,
                    sort,
                    page: {page: adjustedPageNum},
                    fields: columns,
                    reportKey: ENGAGEMENT_SALES,
                })
            );

            const leadListDataStream = listRequestStream.pipe(
                map((response) => updateListData(response))
            );
            const leadListSummaryDataStream = listRequestStream.pipe(
                map((response) => updateListSummaryData(response))
            );

            return leadListDataStream.pipe(
                merge(leadListSummaryDataStream),
                catchError((err) => {
                    const safeError = safelyParseError(err);

                    return Observable.of(failListData(safeError));
                })
            );
        })
    );

function getPostParams(reportParams, filters) {
    const {gap, segment, useConfidence, dateRangeType} = reportParams;

    const filtersWithOriginAndStatus = addReportSpecificFilters(filters);

    return {
        filter: serializeFilters(filtersWithOriginAndStatus),
        groupByField: dateRangeType,
        pivotFacet: 'total',
        reportType: ENGAGEMENT_SALES,
        gap,
        useConfidence,
        segmentByField: segment,
    };
}

function fetchData(params) {
    return Promise.all([fetchReportData(params)]);
}

function fetchReportData(params) {
    const ajaxParams = {
        url: REST_URL,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any);
}
